import { useEffect } from 'react';
import type mapboxgl from 'mapbox-gl';
import type { CountryVisit } from '../../types';
import { MARKER_COLORS } from '../../utils/markerColor';

export const COUNTRY_SOURCE_ID = 'world-countries';
const SOURCE_ID = COUNTRY_SOURCE_ID;
const GEOJSON_URL = '/world-countries.geojson';

const LAYERS: Array<{ id: string; visitType: 'solo' | 'family' | 'group' | 'markanya'; color: string }> = [
  { id: 'countries-solo',     visitType: 'solo',     color: MARKER_COLORS.solo },
  { id: 'countries-group',    visitType: 'group',    color: MARKER_COLORS.group },
  { id: 'countries-family',   visitType: 'family',   color: MARKER_COLORS.family },
  { id: 'countries-markanya', visitType: 'markanya', color: MARKER_COLORS.markanya }, // markanya on top
];

interface CountryLayerProps {
  map: mapboxgl.Map | null;
  mapReady: boolean;
  countries: CountryVisit[];
}

export function CountryLayer({ map, mapReady, countries }: CountryLayerProps) {
  // Add source + layers once
  useEffect(() => {
    if (!map || !mapReady) return;

    if (!map.getSource(SOURCE_ID)) {
      map.addSource(SOURCE_ID, { type: 'geojson', data: GEOJSON_URL });
    }

    LAYERS.forEach(({ id, color }) => {
      if (!map.getLayer(id)) {
        map.addLayer(
          {
            id,
            type: 'fill',
            source: SOURCE_ID,
            paint: {
              'fill-color': color,
              'fill-opacity': ['case', ['in', ['get', 'ISO3166-1-Alpha-2'], ['literal', [] as string[]]], 0.4, 0],
            },
          },
          'waterway-label'
        );
      }
    });

    return () => {
      LAYERS.forEach(({ id }) => { if (map.getLayer(id)) map.removeLayer(id); });
      if (map.getSource(SOURCE_ID)) map.removeSource(SOURCE_ID);
    };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map, mapReady]);

  // Update each layer's filter when countries change
  useEffect(() => {
    if (!map || !mapReady) return;

    LAYERS.forEach(({ id, visitType }) => {
      if (!map.getLayer(id)) return;
      const codes = countries
        .filter((c) => c.visitType === visitType)
        .map((c) => c.countryCode);
      map.setPaintProperty(id, 'fill-opacity', [
        'case',
        ['in', ['get', 'ISO3166-1-Alpha-2'], ['literal', codes]],
        0.4,
        0,
      ]);
    });
  }, [map, mapReady, countries]);

  return null;
}
